import React, { useMemo, useState, useEffect } from "react"
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap"
import axios from "axios"

const initialForm = {
  businessId: "", // will hold the selected business.userID (string -> cast to number on submit)
  consignee: {
    name: "",
    contact1: "",
    contact2: "",
    city: "",
    Address: "",
    email: "",
  },
  order: {
    orderId: "",
    origin: "",
    productDesc: "",
    itemQty: "",
    weight: "",
    service: "",
  },
  payment: {
    codAmount: "",
    taxAmount: "",
    totalCollect: "",
  },
}

export default function CreateCodLeadModal({
  isOpen,
  onClose,
  onCreated, // callback to refresh list
  approvedBusinesses, // [{ id, userID, businessName, businessCity, ... }]
  apiURl,
}) {
  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // Auto-calc totalCollect = COD + Tax
  useEffect(() => {
    const cod = parseFloat(form.payment.codAmount || 0) || 0
    const tax = parseFloat(form.payment.taxAmount || 0) || 0
    const total = cod + tax
    setForm(f => ({
      ...f,
      payment: { ...f.payment, totalCollect: String(total) },
    }))
  }, [form.payment.codAmount, form.payment.taxAmount])

  const setField = (path, value) => {
    const parts = path.split(".")
    setForm(prev => {
      const next = { ...prev }
      let node = next
      for (let i = 0; i < parts.length - 1; i++) {
        node[parts[i]] = { ...node[parts[i]] }
        node = node[parts[i]]
      }
      node[parts[parts.length - 1]] = value
      return next
    })
  }

  const validate = () => {
    const e = {}
    if (!form.businessId) e.businessId = "Required"

    if (!form.consignee.name) e.consignee_name = "Required"
    if (!form.consignee.contact1) e.consignee_contact1 = "Required"
    if (!form.consignee.city) e.consignee_city = "Required"

    if (!form.order.orderId) e.order_orderId = "Required"
    if (!form.order.productDesc) e.order_productDesc = "Required"
    if (!form.order.itemQty) e.order_itemQty = "Required"
    if (!form.order.weight) e.order_weight = "Required"
    if (!form.order.service) e.order_service = "Required"

    if (!form.payment.codAmount) e.payment_codAmount = "Required"

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    const payload = {
      // IMPORTANT: send userID as businessId
      businessId: Number(form.businessId),

      consigneeInfo: {
        name: form.consignee.name,
        contact1: form.consignee.contact1,
        contact2: form.consignee.contact2 || null,
        city: form.consignee.city,
        Address: form.consignee.Address, // capital A to match backend
        email: form.consignee.email || null,
      },
      orderInfo: {
        orderId: form.order.orderId,
        origin: form.order.origin,
        productDesc: form.order.productDesc,
        itemQty: Number(form.order.itemQty),
        weight: Number(form.order.weight),
        service: form.order.service,
      },
      paymentInfo: {
        codAmount: Number(form.payment.codAmount),
        taxAmount: form.payment.taxAmount ? Number(form.payment.taxAmount) : 0,
        totalCollect: form.payment.totalCollect
          ? Number(form.payment.totalCollect)
          : undefined,
      },
    }

    try {
      setSubmitting(true)
      const { data: res } = await axios.post(
        `${apiURl}/codOrderCreation`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )
      if (res.status === "success") {
        onCreated?.()
        onClose()
        setForm(initialForm)
        setErrors({})
      } else {
        alert(res.message || "Failed to create lead")
      }
    } catch (err) {
      console.error(err)
      alert("Error creating COD lead")
    } finally {
      setSubmitting(false)
    }
  }

  // Map Approved businesses → dropdown options
  // Use userID as the option value (this is the app user's id you need to send)
  const businessOptions = useMemo(() => {
    return (approvedBusinesses || []).map(b => ({
      abrId: b.id, // app_business_register.id
      value: String(b.userID), // will be sent as businessId
      label: b.businessName || `#${b.id}`,
      city: b.businessCity || "",
    }))
  }, [approvedBusinesses])

  const selected = businessOptions.find(
    o => String(o.userId) === String(form.businessId)
  )

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Create COD Lead</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          {/* BUSINESS */}
          <Col md="12">
            <h5 style={{ color: "#485EC4" }}>Business</h5>
          </Col>
          <Col md="12">
            <Label>Business (Approved)</Label>
            <Input
              type="select"
              value={form.businessId}
              onChange={e => setField("businessId", e.target.value)}
              invalid={!!errors.businessId}
            >
              <option value="">Select business</option>
              {businessOptions.map(o => (
                <option key={o.abrId} value={o.value}>
                  {o.label}
                  {o.city ? ` — ${o.city}` : ""}
                </option>
              ))}
            </Input>
            {errors.businessId && (
              <FormFeedback className="d-block">
                {errors.businessId}
              </FormFeedback>
            )}
            {selected && (
              <small className="text-muted">
                Selected: <strong>{selected.label}</strong>
                {selected.city ? ` — City: ${selected.city}` : ""} — userID:{" "}
                {selected.userId}
              </small>
            )}
          </Col>

          {/* CONSIGNEE */}
          <Col md="12" className="mt-3">
            <h5 style={{ color: "#485EC4" }}>Consignee Details</h5>
          </Col>
          <Col md="6">
            <Label>Name</Label>
            <Input
              value={form.consignee.name}
              onChange={e => setField("consignee.name", e.target.value)}
              invalid={!!errors.consignee_name}
            />
            <FormFeedback>{errors.consignee_name}</FormFeedback>
          </Col>
          <Col md="3">
            <Label>Contact 1</Label>
            <Input
              value={form.consignee.contact1}
              onChange={e => setField("consignee.contact1", e.target.value)}
              invalid={!!errors.consignee_contact1}
            />
            <FormFeedback>{errors.consignee_contact1}</FormFeedback>
          </Col>
          <Col md="3">
            <Label>Contact 2</Label>
            <Input
              value={form.consignee.contact2}
              onChange={e => setField("consignee.contact2", e.target.value)}
            />
          </Col>
          <Col md="4">
            <Label>City</Label>
            <Input
              value={form.consignee.city}
              onChange={e => setField("consignee.city", e.target.value)}
              invalid={!!errors.consignee_city}
              placeholder={selected?.city || ""}
            />
            <FormFeedback>{errors.consignee_city}</FormFeedback>
          </Col>
          <Col md="8">
            <Label>Full Address</Label>
            <Input
              value={form.consignee.Address}
              onChange={e => setField("consignee.Address", e.target.value)}
              placeholder="Street, Area, City"
            />
          </Col>
          <Col md="6">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.consignee.email}
              onChange={e => setField("consignee.email", e.target.value)}
              placeholder="optional"
            />
          </Col>

          {/* ORDER */}
          <Col md="12" className="mt-3">
            <h5 style={{ color: "#485EC4" }}> Order Details</h5>
          </Col>
          <Col md="4">
            <Label>Order ID</Label>
            <Input
              value={form.order.orderId}
              onChange={e => setField("order.orderId", e.target.value)}
              invalid={!!errors.order_orderId}
            />
            <FormFeedback>{errors.order_orderId}</FormFeedback>
          </Col>
          <Col md="4">
            <Label>Origin</Label>
            <Input
              value={form.order.origin}
              onChange={e => setField("order.origin", e.target.value)}
              placeholder="e.g. Warehouse A"
            />
          </Col>
          <Col md="4">
            <Label>Service Type</Label>
            <Input
              value={form.order.service}
              onChange={e => setField("order.service", e.target.value)}
              invalid={!!errors.order_service}
              placeholder="e.g. overnight"
            />
            <FormFeedback>{errors.order_service}</FormFeedback>
          </Col>
          <Col md="6">
            <Label>Product Description</Label>
            <Input
              value={form.order.productDesc}
              onChange={e => setField("order.productDesc", e.target.value)}
              invalid={!!errors.order_productDesc}
            />
            <FormFeedback>{errors.order_productDesc}</FormFeedback>
          </Col>
          <Col md="3">
            <Label>Item Qty</Label>
            <Input
              type="number"
              min="1"
              value={form.order.itemQty}
              onChange={e => setField("order.itemQty", e.target.value)}
              invalid={!!errors.order_itemQty}
            />
            <FormFeedback>{errors.order_itemQty}</FormFeedback>
          </Col>
          <Col md="3">
            <Label>Weight</Label>
            <Input
              type="number"
              step="0.01"
              value={form.order.weight}
              onChange={e => setField("order.weight", e.target.value)}
              invalid={!!errors.order_weight}
            />
            <FormFeedback>{errors.order_weight}</FormFeedback>
          </Col>

          {/* PAYMENT */}
          <Col md="12" className="mt-3">
            <h5 style={{ color: "#485EC4" }}>Payment</h5>
          </Col>
          <Col md="4">
            <Label>COD Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={form.payment.codAmount}
              onChange={e => setField("payment.codAmount", e.target.value)}
              invalid={!!errors.payment_codAmount}
            />
            <FormFeedback>{errors.payment_codAmount}</FormFeedback>
          </Col>
          <Col md="4">
            <Label>Tax Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={form.payment.taxAmount}
              onChange={e => setField("payment.taxAmount", e.target.value)}
              placeholder="0"
            />
          </Col>
          <Col md="4">
            <Label>Total Collect</Label>
            <Input
              type="number"
              step="0.01"
              value={form.payment.totalCollect}
              onChange={e => setField("payment.totalCollect", e.target.value)}
            />
            <small className="text-muted">
              Auto-calculated (COD + Tax). You can override.
            </small>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : "Create Lead"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
